<template>
    <div>
        <skeleton-loader v-if="loading" type="team-cards" />

        <template v-else>
            <div v-if="teamsWithData.length === 0" class="teams-empty">
                <v-icon size="40" color="txtMuted">mdi-account-group-outline</v-icon>
                <p>{{ $t('common.not_found') }}</p>
            </div>

            <div v-else class="team-list">
                <div v-for="team in teamsWithData" :key="team.id" class="team-card"
                    :class="{ expanded: !!expanded[team.id] }">
                    <div class="team-card-header" @click="toggleTeam(team.id)">
                        <div class="team-color-dot" :style="{ background: team.color }" />
                        <span class="team-name">{{ team.name }}</span>
                        <div class="team-meta">
                            <span class="team-meta-item">
                                <strong>{{ team.memberCount }}</strong> {{ $t('common.students').toLowerCase() }}
                            </span>
                            <span v-if="team.lastEvalDate" class="team-meta-item">
                                {{ $t('common.last_evaluation') }} <strong>{{ team.lastEvalDate }}</strong>
                            </span>
                            <span v-else class="team-meta-item team-meta-item--empty">
                                {{ $t('common.without_evaluations') }}
                            </span>
                        </div>
                        <div class="team-actions" @click.stop>
                            <button-tooltip top :tooltip="$t('tooltips.create_group')">
                                <template v-slot:activator="{ on, attrs }">
                                    <button class="icon-btn" v-bind="attrs" v-on="on"
                                        @click="$emit('click:create:group', team)">
                                        <v-icon size="15">mdi-account-multiple-plus-outline</v-icon>
                                    </button>
                                </template>
                            </button-tooltip>
                            <template v-if="team.assignedEvaluationId">
                                <button-tooltip top :tooltip="$t('tooltips.load_data')">
                                    <template v-slot:activator="{ on, attrs }">
                                        <button class="icon-btn" v-bind="attrs" v-on="on"
                                            @click="$emit('click:load:evaluation', team)">
                                            <v-icon size="15">mdi-pencil-outline</v-icon>
                                        </button>
                                    </template>
                                </button-tooltip>
                                <button-tooltip top :tooltip="$t('tooltips.see_data')">
                                    <template v-slot:activator="{ on, attrs }">
                                        <button class="icon-btn" v-bind="attrs" v-on="on"
                                            @click="$emit('click:evaluation:statistics', team)">
                                            <v-icon size="15">mdi-eye-outline</v-icon>
                                        </button>
                                    </template>
                                </button-tooltip>
                                <button-tooltip top :tooltip="$t('tooltips.delete_evaluation')">
                                    <template v-slot:activator="{ on, attrs }">
                                        <button class="icon-btn danger-btn" v-bind="attrs" v-on="on"
                                            @click="$emit('click:evaluation:delete', team)">
                                            <v-icon size="15">$delete</v-icon>
                                        </button>
                                    </template>
                                </button-tooltip>
                            </template>
                            <button-tooltip v-else top :tooltip="$t('tooltips.add_evaluation')">
                                <template v-slot:activator="{ on, attrs }">
                                    <button class="icon-btn add-btn" v-bind="attrs" v-on="on"
                                        @click="$emit('click:assign', team)">
                                        <v-icon size="16">mdi-plus-circle-outline</v-icon>
                                    </button>
                                </template>
                            </button-tooltip>
                            <button class="team-expand" @click="toggleTeam(team.id)">
                                <v-icon size="14">mdi-chevron-down</v-icon>
                            </button>
                        </div>
                    </div>

                    <v-expand-transition>
                        <div v-if="expanded[team.id]" class="team-members">
                            <div v-if="team.members.length === 0" class="team-members-empty">
                                {{ $t('common.without_student_subgroup_added') }}
                            </div>
                            <template v-else>
                            <div v-if="team.members.length > membersPerPage" class="team-members-search">
                                <kimsa-text-field :value="memberSearch[team.id] || ''"
                                    @change="setMemberSearch(team.id, $event)" :placeholder="`${$t('common.search')}`"
                                    prepend-inner-icon="mdi-magnify" dense />
                            </div>
                            <div v-if="pagedMembers(team).length === 0" class="team-members-empty">
                                {{ $t('common.not_found') }}
                            </div>
                            <div v-for="member in pagedMembers(team)" :key="member.id" class="team-member-row">
                                <student-avatar :avatar="member.avatar" :name="member.name" :avatar-key="member.id"
                                    :size="28" :radius="6" />
                                <span class="team-member-name">{{ member.name }}</span>
                                <span class="team-member-date" :class="{ 'team-member-date--empty': !member.lastDate }">
                                    {{ member.lastDate || '—' }}
                                </span>
                                <div class="row-actions">
                                    <template v-if="team.assignedEvaluationId">
                                        <button-tooltip top :tooltip="$t('tooltips.create_report')">
                                            <template v-slot:activator="{ on, attrs }">
                                                <button class="icon-btn" v-bind="attrs" v-on="on"
                                                    @click="onMemberReport(member, team)">
                                                    <v-icon size="15">mdi-file-document-edit-outline</v-icon>
                                                </button>
                                            </template>
                                        </button-tooltip>
                                        <button-tooltip top :tooltip="$t('tooltips.see_report_history')">
                                            <template v-slot:activator="{ on, attrs }">
                                                <button class="icon-btn" v-bind="attrs" v-on="on"
                                                    @click="onMemberHistory(member, team)">
                                                    <v-icon size="15">mdi-file-document-refresh-outline</v-icon>
                                                </button>
                                            </template>
                                        </button-tooltip>
                                    </template>
                                    <button-tooltip top :tooltip="$t('common.delete')">
                                        <template v-slot:activator="{ on, attrs }">
                                            <button class="icon-btn danger-btn" v-bind="attrs" v-on="on"
                                                @click="onMemberDelete(member, team)">
                                                <v-icon size="15">$delete</v-icon>
                                            </button>
                                        </template>
                                    </button-tooltip>
                                </div>
                            </div>
                            <div v-if="memberPageCount(team) > 1" class="team-members-footer">
                                <span class="pagination-info">
                                    {{ $tc('common.students_count', filteredMembers(team).length, { n: filteredMembers(team).length }) }}
                                </span>
                                <kimsa-pagination :value="memberPage(team.id)"
                                    @page="setMemberPage(team.id, $event)" :pageCount="memberPageCount(team)"
                                    :total-visible="isMobile ? 4 : 7" height="auto" no-right />
                            </div>
                            </template>
                        </div>
                    </v-expand-transition>
                </div>
            </div>

            <div v-if="ungroupedStudents.length > 0" class="sin-group-section">
                <div class="sin-group-header" @click="noGroupOpen = !noGroupOpen">
                    <span class="sin-group-title">{{ $t('common.without_student_subgroup') }}</span>
                    <span class="sin-group-count">{{ ungroupedStudents.length }}</span>
                    <div class="sin-group-toolbar" @click.stop>
                        <div class="sin-group-search">
                            <kimsa-text-field @change="search = $event" :value="search"
                                :placeholder="`${$t('common.search')}`" prepend-inner-icon="mdi-magnify" dense />
                        </div>
                    </div>
                    <v-icon size="18" class="sin-group-chevron" :class="{ open: noGroupOpen }">mdi-chevron-down</v-icon>
                </div>
                <v-expand-transition>
                    <div v-show="noGroupOpen">
                        <div class="sin-group-list">
                            <div v-if="paginatedUngrouped.length === 0" class="team-members-empty">
                                {{ $t('common.not_found') }}
                            </div>
                            <div v-for="student in paginatedUngrouped" :key="student.id" class="sin-group-row">
                                <student-avatar :avatar="student.avatar" :name="student.name" :avatar-key="student.id"
                                    :size="28" :radius="6" />
                                <span class="team-member-name">{{ student.name }}</span>
                                <button class="btn-add-group" @click="openAddToGroup(student.id)">
                                    + {{ $t('common.add_to_group') }}
                                </button>
                            </div>
                        </div>
                        <div class="sin-group-footer">
                            <span class="pagination-info">
                                {{ $tc('common.students_count', filteredUngrouped.length, { n: filteredUngrouped.length }) }}
                            </span>
                            <kimsa-pagination v-if="ungroupedPageCount > 1" :value="page" @page="page = $event"
                                :pageCount="ungroupedPageCount" :total-visible="isMobile ? 4 : 7" height="auto" no-right />
                        </div>
                    </div>
                </v-expand-transition>
            </div>
        </template>

        <select-evaluation-group-student-dialog hide-activator :teams="teams" :student-id="addToGroupStudentId"
            @save="updateGroup($event)" ref="addToGroup" />
    </div>
</template>

<script>
import { mapState } from 'vuex'
import { getAllByIds } from '@/utils/Firebase'
import SelectEvaluationGroupStudentDialog from '../coach/SelectEvaluationGroupStudentDialog.vue'
import KimsaTextField from '../common/inputs/KimsaTextField.vue'
import KimsaPagination from '../common/KimsaPagination.vue'
import SkeletonLoader from '../common/SkeletonLoader.vue'
import StudentAvatar from '../common/StudentAvatar.vue'
import ButtonTooltip from '../common/ButtonTooltip.vue'

const TEAM_COLORS = ['#66CCCC', '#CCFF33', '#009966', '#E85C5C', '#8B7CF6', '#3B9EFF', '#F5A623']

export default {
    components: {
        SelectEvaluationGroupStudentDialog, KimsaTextField, KimsaPagination, SkeletonLoader, StudentAvatar, ButtonTooltip,
    },
    props: {
        students: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            loading: true,
            currentStudents: [],
            teamEvalMap: {},
            expanded: {},
            memberPages: {},
            memberSearch: {},
            membersPerPage: 10,
            noGroupOpen: false,
            search: '',
            page: 1,
            perPage: 8,
            addToGroupStudentId: null,
        }
    },
    computed: {
        ...mapState(['teams']),
        teamsWithData(vm = this) {
            return vm.teams.map((team, index) => {
                const values = vm.teamEvalMap[team.assignedEvaluationId]?.values || []
                const members = vm.currentStudents
                    .filter(student => student?.evaluationTeam?.id === team.id)
                    .map(student => ({
                        ...student,
                        groupId: student.evaluationTeam?.subgroupId ?? null,
                        lastDate: vm.lastDateForStudent(values, student.id),
                    }))
                return {
                    ...team,
                    id: team.id,
                    color: TEAM_COLORS[index % TEAM_COLORS.length],
                    members,
                    memberCount: members.length,
                    lastEvalDate: vm.maxDate(values),
                }
            })
        },
        ungroupedStudents(vm = this) {
            return vm.currentStudents.filter(student => !student?.evaluationTeam?.id)
        },
        filteredUngrouped(vm = this) {
            if (!vm.search) return vm.ungroupedStudents
            const query = vm.search.toLowerCase()
            return vm.ungroupedStudents.filter(student => (student.name || '').toLowerCase().includes(query))
        },
        ungroupedPageCount(vm = this) {
            return Math.ceil(vm.filteredUngrouped.length / vm.perPage)
        },
        paginatedUngrouped(vm = this) {
            const start = (vm.page - 1) * vm.perPage
            return vm.filteredUngrouped.slice(start, start + vm.perPage)
        },
    },
    mounted() {
        const vm = this
        vm.currentStudents = vm.students
        vm.loadTeamEvaluations()
    },
    methods: {
        async loadTeamEvaluations() {
            const vm = this
            await vm.withLoading(async () => {
                const ids = vm.teams.map(team => team.assignedEvaluationId).filter(Boolean)
                if (!ids.length) {
                    vm.teamEvalMap = {}
                    return
                }
                const docs = await getAllByIds('assignedEvaluations', ids)
                const map = {}
                docs.forEach(doc => { map[doc.id] = doc })
                vm.teamEvalMap = map
            })
        },
        toggleTeam(id) {
            const vm = this
            vm.$set(vm.expanded, id, !vm.expanded[id])
        },
        memberPage(teamId) {
            return this.memberPages[teamId] || 1
        },
        setMemberPage(teamId, page) {
            this.$set(this.memberPages, teamId, page)
        },
        setMemberSearch(teamId, val) {
            this.$set(this.memberSearch, teamId, val)
            this.$set(this.memberPages, teamId, 1)
        },
        filteredMembers(team) {
            const query = (this.memberSearch[team.id] || '').toLowerCase()
            if (!query) return team.members
            return team.members.filter(member => (member.name || '').toLowerCase().includes(query))
        },
        memberPageCount(team) {
            return Math.ceil(this.filteredMembers(team).length / this.membersPerPage) || 1
        },
        pagedMembers(team) {
            const list = this.filteredMembers(team)
            const page = Math.min(this.memberPage(team.id), this.memberPageCount(team))
            const start = (page - 1) * this.membersPerPage
            return list.slice(start, start + this.membersPerPage)
        },
        maxDate(values) {
            const vm = this
            if (!values.length) return null
            let latest = null
            values.forEach(entry => {
                if (!latest || vm.$moment(entry.date, 'DD-MM-YYYY').isAfter(vm.$moment(latest, 'DD-MM-YYYY'))) {
                    latest = entry.date
                }
            })
            return latest ? vm.$moment(latest, 'DD-MM-YYYY').format('DD/MM/YYYY') : null
        },
        lastDateForStudent(values, studentId) {
            const vm = this
            let latest = null
            values.forEach(entry => {
                if (entry[studentId] && (!latest || vm.$moment(entry.date, 'DD-MM-YYYY').isAfter(vm.$moment(latest, 'DD-MM-YYYY')))) {
                    latest = entry.date
                }
            })
            return latest ? vm.$moment(latest, 'DD-MM-YYYY').format('DD/MM/YYYY') : null
        },
        resolveGroup(team, member) {
            return team.evaluationGroups?.find(group => group.id === member.groupId) || null
        },
        onMemberReport(member, team) {
            this.$emit('click:create:report', member, team, this.resolveGroup(team, member))
        },
        onMemberHistory(member, team) {
            this.$emit('click:show:report', member, team, this.resolveGroup(team, member))
        },
        onMemberDelete(member, team) {
            this.$emit('click:student:delete', member, team, this.resolveGroup(team, member))
        },
        openAddToGroup(studentId) {
            const vm = this
            vm.addToGroupStudentId = studentId
            vm.$refs.addToGroup.show()
        },
        updateGroup({ studentId, evaluationTeam }) {
            const vm = this
            let index = vm.currentStudents.findIndex(student => student.id === studentId)
            if (index !== -1) vm.$set(vm.currentStudents[index], 'evaluationTeam', evaluationTeam)
        },
    },
    watch: {
        students(val) {
            this.currentStudents = val
        },
        teams() {
            this.loadTeamEvaluations()
        },
        filteredUngrouped() {
            if (this.page > this.ungroupedPageCount) this.page = 1
        },
        search(val) {
            this.page = 1
            if (val) this.noGroupOpen = true
        },
    }
}
</script>

<style scoped>
.team-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.team-card {
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 14px;
    overflow: hidden;
    box-shadow: 0 1px 0 0 var(--v-outline2-base) inset;
    transition: border-color 0.15s;
}

.team-card:hover {
    border-color: var(--v-outline2-base);
}

.team-card-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 16px;
    cursor: pointer;
    user-select: none;
}

.team-color-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    flex-shrink: 0;
}

.team-name {
    font-size: 15px;
    font-weight: 700;
    color: var(--v-txtPrimary-base);
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.team-meta {
    display: flex;
    align-items: center;
    gap: 16px;
    flex-shrink: 0;
}

.team-meta-item {
    font-size: 12px;
    color: var(--v-txtMuted-base);
    white-space: nowrap;
}

.team-meta-item strong {
    color: var(--v-txtSecondary-base);
    font-weight: 600;
}

.team-meta-item--empty {
    color: var(--v-error-base);
}

.team-actions {
    display: flex;
    gap: 4px;
    align-items: center;
    flex-shrink: 0;
}

.icon-btn {
    width: 30px;
    height: 30px;
    border-radius: 7px;
    border: none;
    background: transparent;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--v-txtMuted-base);
    transition: background 0.12s, color 0.12s;
}

.icon-btn:hover {
    background: var(--v-outline2-base);
    color: var(--v-txtPrimary-base);
}

.icon-btn.add-btn {
    color: var(--v-primary-base);
}

.theme--light .icon-btn.add-btn {
    color: var(--v-accent-base);
}

.icon-btn.add-btn:hover {
    background: rgba(204, 255, 51, 0.12);
    color: var(--v-primary-base);
}

.theme--light .icon-btn.add-btn:hover {
    background: rgba(51, 153, 153, 0.12);
    color: var(--v-accent-base);
}

.icon-btn.danger-btn:hover {
    background: rgba(232, 92, 92, 0.12);
    color: var(--v-error-base);
}

.team-expand {
    width: 28px;
    height: 28px;
    border-radius: 6px;
    border: none;
    background: var(--v-surface2-base);
    color: var(--v-txtMuted-base);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 0.12s;
}

.team-expand:hover {
    background: var(--v-outline2-base);
}

.team-expand .v-icon {
    transition: transform 0.2s;
}

.team-card.expanded .team-expand .v-icon {
    transform: rotate(180deg);
}

.team-members {
    border-top: 1px solid var(--v-outline-base);
}

.team-members-empty {
    padding: 16px;
    text-align: center;
    font-size: 13px;
    color: var(--v-txtMuted-base);
}

.team-members-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 10px 16px;
    border-top: 1px solid var(--v-outline-base);
}

.team-members-search {
    padding: 10px 16px;
    border-bottom: 1px solid var(--v-outline-base);
}

.team-member-row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 16px;
    border-bottom: 1px solid var(--v-outline-base);
    transition: background 0.12s;
}

.team-member-row:last-child {
    border-bottom: none;
}

.team-member-row:hover {
    background: var(--v-surface2-base);
}

.team-member-name {
    flex: 1;
    min-width: 0;
    font-size: 13px;
    font-weight: 500;
    color: var(--v-txtPrimary-base);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    transition: color 0.15s;
}

.team-member-row:hover .team-member-name,
.sin-group-row:hover .team-member-name {
    color: var(--v-primary-base);
}

.theme--light .team-member-row:hover .team-member-name,
.theme--light .sin-group-row:hover .team-member-name {
    color: var(--v-accent-base);
}

.team-member-date {
    font-size: 12px;
    color: var(--v-txtMuted-base);
    white-space: nowrap;
    margin-right: 4px;
}

.team-member-date--empty {
    color: var(--v-outline2-base);
}

.row-actions {
    display: flex;
    gap: 2px;
    align-items: center;
    flex-shrink: 0;
}

.teams-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    padding: 48px 16px;
    color: var(--v-txtMuted-base);
    font-size: 14px;
}

.sin-group-section {
    margin-top: 24px;
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 12px;
    overflow: hidden;
}

.sin-group-header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 14px;
    cursor: pointer;
    transition: background 0.12s;
}

.sin-group-header:hover {
    background: var(--v-surface2-base);
}

.sin-group-title {
    font-size: 13px;
    font-weight: 600;
    color: var(--v-txtPrimary-base);
}

.sin-group-count {
    font-size: 11px;
    font-weight: 600;
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 6px;
    padding: 2px 8px;
    color: var(--v-txtMuted-base);
}

.sin-group-toolbar {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-left: auto;
}

.sin-group-search {
    width: 180px;
    max-width: 40vw;
}

.sin-group-chevron {
    color: var(--v-txtMuted-base);
    transition: transform 0.2s;
    flex-shrink: 0;
}

.sin-group-chevron.open {
    transform: rotate(180deg);
}

.sin-group-list {
    max-height: 320px;
    overflow-y: auto;
    border-top: 1px solid var(--v-outline-base);
}

.sin-group-row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    border-bottom: 1px solid var(--v-outline-base);
    transition: background 0.12s;
}

.sin-group-row:last-child {
    border-bottom: none;
}

.sin-group-row:hover {
    background: var(--v-surface2-base);
}

.btn-add-group {
    margin-left: auto;
    flex-shrink: 0;
    height: 28px;
    padding: 0 12px;
    background: linear-gradient(135deg, rgba(204, 255, 51, 0.15), rgba(51, 153, 153, 0.15));
    border: 1px solid rgba(204, 255, 51, 0.4);
    border-radius: 8px;
    cursor: pointer;
    font-size: 12px;
    font-weight: 600;
    color: var(--v-primary-base);
    transition: opacity 0.15s;
}

.btn-add-group:hover {
    opacity: 0.8;
}

.theme--light .btn-add-group {
    background: rgba(51, 153, 153, 0.1);
    border-color: rgba(51, 153, 153, 0.35);
    color: var(--v-accent-base);
}

.sin-group-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 10px 14px;
    border-top: 1px solid var(--v-outline-base);
}

.pagination-info {
    font-size: 12px;
    color: var(--v-txtMuted-base);
    white-space: nowrap;
}
</style>
